import React, { useEffect, useState } from 'react';
import { ModuleDoc } from '../context/ModulesContext';

interface NewModuleWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (module: ModuleDoc) => void;
}

type Step = 1 | 2 | 3;

function toKebab(name: string): string {
  return name
    .trim()
    .replace(/[^a-zA-Z0-9\s]/g, '')
    .split(/\s+/)
    .filter(Boolean)
    .join('-')
    .toLowerCase();
}

export default function NewModuleWizard({ isOpen, onClose, onCreated }: NewModuleWizardProps) {
  const [step, setStep] = useState<Step>(1);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [hasUserView, setHasUserView] = useState(true);
  const [hasAdminView, setHasAdminView] = useState(false);
  const [route, setRoute] = useState('');
  const [adminRoute, setAdminRoute] = useState('');
  const [routeTouched, setRouteTouched] = useState(false);
  const [adminRouteTouched, setAdminRouteTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setStep(1);
    setName('');
    setDescription('');
    setHasUserView(true);
    setHasAdminView(false);
    setRoute('');
    setAdminRoute('');
    setRouteTouched(false);
    setAdminRouteTouched(false);
    setSubmitting(false);
    setError(null);
  }, [isOpen]);

  // Auto-suggest routes on step 3 entry, unless user has manually edited them
  useEffect(() => {
    if (step !== 3) return;
    const kebab = toKebab(name);
    if (!kebab) return;
    if (hasUserView && !routeTouched) setRoute(`/${kebab}`);
    if (hasAdminView && !adminRouteTouched) setAdminRoute(`/manage-${kebab}`);
  }, [step, name, hasUserView, hasAdminView, routeTouched, adminRouteTouched]);

  if (!isOpen) return null;

  const step1Valid = name.trim().length >= 2 && description.trim().length >= 10;
  const step2Valid = hasUserView || hasAdminView;
  const routeValid = (path: string) => /^\/[a-z0-9/-]+$/.test(path);
  const step3Valid =
    (!hasUserView || (route.length > 0 && routeValid(route))) &&
    (!hasAdminView || (adminRoute.length > 0 && routeValid(adminRoute)));

  const handleCreate = async () => {
    if (submitting || !step3Valid) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/modules`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          description: description.trim(),
          route: hasUserView ? route.trim() : null,
          adminRoute: hasAdminView ? adminRoute.trim() : null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data?.error || 'Failed to create module.');
        setSubmitting(false);
        return;
      }
      onCreated(data as ModuleDoc);
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Network error. Please try again.');
      setSubmitting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 10px',
    background: '#111',
    color: '#fff',
    border: '1px solid #333',
    borderRadius: 4,
    fontSize: '0.9rem',
    boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '0.85rem',
    fontWeight: 600,
    marginBottom: 6,
    color: '#cbd5e1',
  };

  return (
    <div className="smrtdocs-modal-overlay">
      <div className="smrtdocs-modal" style={{ width: '480px' }}>
        <div className="smrtdocs-modal-header" style={{ padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: '1rem' }}>New Module — Step {step} of 3</h2>
          <button
            onClick={() => !submitting && onClose()}
            aria-label="Close"
            disabled={submitting}
            style={{ background: 'transparent', border: 'none', color: '#cbd5e1', fontSize: '1.2rem', cursor: submitting ? 'not-allowed' : 'pointer', lineHeight: 1, padding: '2px 6px' }}
          >
            ✕
          </button>
        </div>

        <div className="smrtdocs-modal-body" style={{ padding: '14px 16px' }}>
          {step === 1 && (
            <>
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Module Name</label>
                <input
                  style={inputStyle}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. SMRT Tasks"
                  autoFocus
                />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Description</label>
                <textarea
                  style={{ ...inputStyle, resize: 'vertical', minHeight: 72 }}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Short description of what this module does"
                />
                <p style={{ fontSize: '0.75rem', color: '#9aa0a6', margin: '4px 0 0' }}>
                  Minimum 10 characters.
                </p>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <p style={{ fontSize: '0.9rem', margin: '0 0 12px' }}>Which views does this module have?</p>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, cursor: 'pointer' }}>
                <input type="checkbox" checked={hasUserView} onChange={(e) => setHasUserView(e.target.checked)} />
                <span style={{ fontSize: '0.9rem' }}>User view</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, cursor: 'pointer' }}>
                <input type="checkbox" checked={hasAdminView} onChange={(e) => setHasAdminView(e.target.checked)} />
                <span style={{ fontSize: '0.9rem' }}>Admin view</span>
              </label>
              {!step2Valid && (
                <p style={{ fontSize: '0.8rem', color: '#ef9a9a', margin: 0 }}>
                  At least one view must be selected.
                </p>
              )}
            </>
          )}

          {step === 3 && (
            <>
              {hasUserView && (
                <div style={{ marginBottom: 12 }}>
                  <label style={labelStyle}>User Route</label>
                  <input
                    style={inputStyle}
                    value={route}
                    onChange={(e) => { setRoute(e.target.value); setRouteTouched(true); }}
                    placeholder="/example"
                  />
                </div>
              )}
              {hasAdminView && (
                <div style={{ marginBottom: 12 }}>
                  <label style={labelStyle}>Admin Route</label>
                  <input
                    style={inputStyle}
                    value={adminRoute}
                    onChange={(e) => { setAdminRoute(e.target.value); setAdminRouteTouched(true); }}
                    placeholder="/manage-example"
                  />
                </div>
              )}
              <p style={{ fontSize: '0.75rem', color: '#9aa0a6', margin: 0 }}>
                Routes must start with "/" and contain only lowercase letters, numbers, and dashes.
              </p>
            </>
          )}

          {error && (
            <p style={{ fontSize: '0.85rem', color: '#ef9a9a', margin: '12px 0 0', padding: '8px 10px', background: 'rgba(229, 57, 53, 0.12)', borderRadius: 4 }}>
              {error}
            </p>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 18 }}>
            <button
              className="btn-cancel"
              onClick={() => (step === 1 ? onClose() : setStep((s) => (s - 1) as Step))}
              disabled={submitting}
              style={{ opacity: submitting ? 0.5 : 1, cursor: submitting ? 'not-allowed' : 'pointer' }}
            >
              {step === 1 ? 'Cancel' : 'Back'}
            </button>

            {step < 3 ? (
              <button
                className="btn-save"
                onClick={() => setStep((s) => (s + 1) as Step)}
                disabled={(step === 1 && !step1Valid) || (step === 2 && !step2Valid)}
                style={{ opacity: (step === 1 && !step1Valid) || (step === 2 && !step2Valid) ? 0.5 : 1 }}
              >
                Next
              </button>
            ) : (
              <button
                className="btn-save"
                onClick={handleCreate}
                disabled={!step3Valid || submitting}
                style={{
                  opacity: !step3Valid || submitting ? 0.7 : 1,
                  cursor: !step3Valid || submitting ? 'not-allowed' : 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                {submitting && <span className="spinner" />}
                {submitting ? 'Creating…' : 'Create Module'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
